import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Shield, Users, FileText, Calendar, MessageCircle, Mic, ArrowRight } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

export default function LoginPage() {
  const navigate = useNavigate();

  const handleGoogleLogin = async () => {
    try {
      const response = await axios.get('/api/auth/google/url');
      if (response.data.authorization_url) {
        window.location.href = response.data.authorization_url;
        return;
      }
    } catch (error) {
      // 18/09/2026 — niente piu' ripiego su servizi esterni di login.
      console.error("Google login non disponibile:", error);
      toast.error("Accesso Google non disponibile in questo momento. Riprova tra poco.");
    }
  };

  const features = [
    { icon: Users, title: "Gestione Clienti", desc: "Database completo dei tuoi clienti" },
    { icon: FileText, title: "Polizze", desc: "Traccia tutte le polizze attive" },
    { icon: Calendar, title: "Scadenze", desc: "Non perdere mai una scadenza" },
    { icon: MessageCircle, title: "Telegram", desc: "Comunica con i clienti" },
    { icon: Mic, title: "Dettatura Vocale", desc: "Inserisci dati con la voce" },
  ];

  return (
    <div className="h-screen bg-background flex overflow-hidden" data-testid="login-page">
      {/* Left Side - Hero Image */}
      <div className="hidden lg:flex lg:w-[55%] relative bg-muted items-center justify-center p-12">
        <img
          src="/login-hero.jpg"
          alt="Insurance Protection"
          className="object-contain max-h-full max-w-full rounded-lg shadow-2xl"
        />
        <div className="absolute bottom-10 left-10 right-10 bg-card/90 backdrop-blur-md p-5 rounded-lg border border-border/50 shadow-lg">
          <h2 className="text-lg font-semibold text-foreground mb-1 font-heading">
            Pronto a proteggerti da ogni evenienza?
          </h2>
          <p className="text-sm text-muted-foreground">
            Polizze, clienti e scadenze sempre sotto controllo.
          </p>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-[45%] flex flex-col justify-center px-8 md:px-16 lg:px-20 overflow-y-auto">
        <div className="max-w-md w-full mx-auto py-8">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-12">
            <div className="w-11 h-11 bg-primary rounded-lg flex items-center justify-center shadow-sm">
              <Shield className="w-6 h-6 text-primary-foreground" strokeWidth={2.2} />
            </div>
            <div>
              <h1 className="text-lg font-bold font-heading text-foreground leading-tight">InsureAssist</h1>
              <p className="text-xs text-muted-foreground">Broker Dashboard</p>
            </div>
          </div>

          {/* Welcome Text */}
          <div className="mb-8">
            <h2 className="text-3xl font-bold font-heading text-foreground tracking-tight">
              Benvenuto
            </h2>
            <p className="text-muted-foreground mt-2">
              Accedi per gestire i tuoi clienti e le tue polizze
            </p>
          </div>

          {/* Google Login Button */}
          <Button
            data-testid="google-login-btn"
            onClick={handleGoogleLogin}
            className="w-full h-12 text-base bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg mb-8 transition-all duration-200 active:scale-[0.98] shadow-sm"
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Accedi con Google
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>

          {/* Divider */}
          <div className="flex items-center gap-3 mb-6">
            <div className="h-px bg-border flex-1" />
            <span className="text-xs text-muted-foreground uppercase tracking-wider">Funzionalit&agrave;</span>
            <div className="h-px bg-border flex-1" />
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-2 gap-3">
            {features.map((feature, index) => (
              <div
                key={feature.title}
                className="p-3 bg-card border border-border rounded-lg animate-fadeIn card-hover cursor-default"
                style={{ animationDelay: `${index * 0.06}s` }}
              >
                <feature.icon className="w-4 h-4 text-primary mb-1.5" strokeWidth={1.8} />
                <h3 className="font-semibold text-xs text-foreground font-heading">{feature.title}</h3>
                <p className="text-[11px] text-muted-foreground leading-snug mt-0.5">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
